import { createFileRoute, Outlet, useNavigate, Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { checkIsAdmin } from "@/lib/articles.functions";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: `Admin — ${SITE.name}` },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminLayout,
});

function AdminLayout() {
  const navigate = useNavigate();
  const router = useRouterState();
  const [state, setState] = useState<"checking" | "ok" | "denied">("checking");

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        navigate({ to: "/auth" });
        return;
      }
      try {
        const res = await checkIsAdmin();
        if (!active) return;
        setState(res.isAdmin ? "ok" : "denied");
      } catch {
        if (active) setState("denied");
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }

  if (state === "checking") {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Checking access…</div>;
  }
  if (state === "denied") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="headline-display text-3xl">Not an editor</h1>
          <p className="mt-3 text-muted-foreground">
            Your account doesn't have admin access. Contact the publisher.
          </p>
          <button onClick={signOut} className="mt-6 underline">Sign out</button>
        </div>
      </div>
    );
  }

  const path = router.location.pathname;
  const tab = (to: string, label: string, exact = false) => (
    <Link
      to={to}
      className={
        "px-3 py-1.5 text-sm border-b-2 transition-colors " +
        ((exact ? path === to : path.startsWith(to)) ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")
      }
    >
      {label}
    </Link>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-paper-soft">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="headline-display text-xl">{SITE.name}<span className="text-muted-foreground"> / admin</span></Link>
          <button onClick={signOut} className="text-sm text-muted-foreground hover:text-foreground">Sign out</button>
        </div>
        <div className="mx-auto max-w-6xl px-5 sm:px-8 flex gap-4">
          {tab("/admin", "Dashboard", true)}
          {tab("/admin/inbox", "Inbox")}
          {tab("/admin/ideas", "Ideas")}
          {tab("/admin/generate", "Quick generate")}
          {tab("/admin/schedule", "Schedule")}
          {tab("/admin/backfill", "Backfill")}
          {tab("/admin/articles", "Articles")}
          {tab("/admin/topics", "Topics")}
          {tab("/admin/subscribers", "Subscribers")}
          {tab("/admin/authors", "Authors")}
          {tab("/admin/launch-kits", "Launch kits")}
          {tab("/admin/new", "New article")}
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}
